// Core imports
import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component Imports
import { Card, Col, Row } from 'antd';
import SummaryEngagementSection from './SummaryEngagementSection';
import SummarySessionsSection from './SummarySessionsSection';
import SummaryUserInteractionsSection from './SummaryUserInteractionsSection';
import SummaryUsersSection from './SummaryUsersSection';

// Misc imports
import { useFetchAnalyticsSummaryQuery } from '../../store/analytics/analyticsApi';
import { ANALYTICS_SECTIONS } from '../../constants/main';
import { isEmptyArray } from '../../utils/is/isEmptyArray';

const AnalyticsSummaryTab = ({
  requestParams,
  setIsLoading = () => false
}) => {
  const { data: analyticsSummary, isFetching: isFetchingAnalyticsSummary, isUninitialized } = useFetchAnalyticsSummaryQuery({ ...requestParams?.[0] }, { skip: isEmptyArray(requestParams) });

  // Memoize loading state to prevent unnecessary re-renders
  const isLoading = useMemo(() =>
    isFetchingAnalyticsSummary || isUninitialized,
  [isFetchingAnalyticsSummary, isUninitialized]
  );

  useEffect(() => {
    setIsLoading(isLoading);

    return () => {
      setIsLoading(false);
    };
  }, [isLoading]);

  const renderSummarySection = () => {
    return (
      <Row gutter={[16, 16]} className="mb-5 hide-empty" id={`analytics-section_${ANALYTICS_SECTIONS.SUMMARY.key}`}>
        <Col xs={24} md={12} xxl={6} id={`analytics-section_${ANALYTICS_SECTIONS.SUMMARY.key}_${ANALYTICS_SECTIONS.SUMMARY.subSections.ENGAGEMENT.key}`}>
          <Card className="h-100">
            <SummaryEngagementSection
              data={analyticsSummary?.engagement}
              isLoading={isLoading}
              sectionTitle={t('Engagement')}
            />
          </Card>
        </Col>

        <Col xs={24} md={12} xxl={6} id={`analytics-section_${ANALYTICS_SECTIONS.SUMMARY.key}_${ANALYTICS_SECTIONS.SUMMARY.subSections.SESSIONS.key}`}>
          <Card className="h-100">
            <SummarySessionsSection
              data={analyticsSummary?.sessions}
              isLoading={isLoading}
              sectionTitle={t('Sessions')}
              seriesName="Sessions"
            />
          </Card>
        </Col>

        <Col xs={24} md={12} xxl={6} id={`analytics-section_${ANALYTICS_SECTIONS.SUMMARY.key}_${ANALYTICS_SECTIONS.SUMMARY.subSections.USER_INTERACTIONS.key}`}>
          <Card className="h-100">
            <SummaryUserInteractionsSection
              data={analyticsSummary?.interactions}
              isLoading={isLoading}
              sectionTitle={t('User Interactions')}
              seriesName="Interactions"
            />
          </Card>
        </Col>

        <Col xs={24} md={12} xxl={6} id={`analytics-section_${ANALYTICS_SECTIONS.SUMMARY.key}_${ANALYTICS_SECTIONS.SUMMARY.subSections.USERS.key}`}>
          <Card className="h-100">
            <SummaryUsersSection
              data={analyticsSummary?.users}
              isLoading={isLoading}
              sectionTitle={t('Users')}
              seriesName="Total Users"
            />
          </Card>
        </Col>
      </Row>
    );
  };
  return (
    <Fragment>
      { renderSummarySection() }
    </Fragment>
  );
};

AnalyticsSummaryTab.propTypes = {
  requestParams: PropTypes.array,
  setIsLoading: PropTypes.func
};

export default AnalyticsSummaryTab;
